// $FlowFixMe
import React, { useState, useEffect } from 'react'; // eslint-disable-line
import BigCalendar from 'react-big-calendar';
import moment from 'moment';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import type {
  Activity,
  Assignee,
  Prospect,
} from '../../containers/ProspectProfile/types';
import * as prospectActions from '../../containers/PeopleProfile/actions';
import Spinner from '../../components/Spinner';
import { editCompletedActivity } from '../../components/EditCompletedActivityModal';
import { editPendingActivity } from '../../components/EditPendingActivityModal';
import messages from './messages';
import styled from 'styled-components';
import { curryN, takeLast } from 'ramda';
import { recordActivity, scheduleActivity } from '../ActivityModal';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getAllActivityTypes } from '../../containers/App/actions';
import {
  getAllActivitiesByAssignee,
  clearActivitiesByAssignee,
} from '../../containers/Home/actions';
import type { ActivityType } from '../../containers/App/types';
import { createActivity } from '../CreateActivityModal';

import { useAsyncAssigneeEvents } from './hooks';

import 'react-big-calendar/lib/css/react-big-calendar.css';

export const SCHEDULED_COLOR = '#7734E7';
export const RECORDED_COLOR = '#4D73FF';
export const EXPANDED_EVENTS_POPUP_OFFSET = { x: 0, y: 4 };
export const EXPANDED_EVENTS_POPUP_MAX_HEIGHT = '50vh';
export const EXPANDED_EVENTS_POPUP_MIN_HEIGHT = 160;
export const EXPANDED_EVENTS_POPUP_VIEWPORT_PADDING = 120;
export const ASSIGNEE_CALENDAR_CONTAINER_STYLE = { height: '100%' };

BigCalendar.setLocalizer(BigCalendar.momentLocalizer(moment));

const Calendar = styled(BigCalendar)`
  width: 100%;

  table[class$='rbc-agenda-table'] > tbody > tr > td {
    width: 33%;
  }

  table[class$='rbc-agenda-table'] > thead > tr > th {
    width: 33% !important;
  }

  .rbc-event-label {
    display: none;
  }

  .rbc-event-content {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

type Props = {
  actions: any,
  activities: Array<Activity>,
  activityTypes: Array<ActivityType>,
  assignees: Array<Assignee>,
  defaultView: string,
  intl: Object,
  locale: string,
  selectedAssignee: Assignee,
  onChangeAssignee: Function,
  store: any,
  currentFilter: string,
};

export const showMore = (message: string, total: number) =>
  `+${total} ${message}`;

const generateMessages = (intl: Object) => {
  return {
    allDay: intl.formatMessage(messages.allDay),
    previous: intl.formatMessage(messages.previous),
    next: intl.formatMessage(messages.next),
    today: intl.formatMessage(messages.today),
    month: intl.formatMessage(messages.month),
    week: intl.formatMessage(messages.week),
    day: intl.formatMessage(messages.day),
    agenda: intl.formatMessage(messages.agenda),
    date: intl.formatMessage(messages.date),
    time: intl.formatMessage(messages.time),
    event: intl.formatMessage(messages.event),
    showMore: curryN(2, showMore)(intl.formatMessage(messages.showMore)),
  };
};

export const eventStyleGetter = (event: Object) => {
  const activityCompletionStatusId =
    event?.activity?.activityCompletionStatusId;
  var style = {
    backgroundColor: activityCompletionStatusId
      ? RECORDED_COLOR
      : SCHEDULED_COLOR,
    borderRadius: '0px',
    opacity: 0.8,
    border: '0px',
    display: 'block',
  };
  return { style };
};

export const updateTooltip = (e: Object, props, currentView) => {
  const month = `${e.timeRange} ${e.toolTip}`;
  const weekAndDayTooltip = `${e.toolTip}`;
  if (
    (props.defaultView === 'month' && currentView === null) ||
    currentView === 'month'
  ) {
    return month;
  } else {
    return weekAndDayTooltip;
  }
};

export const generateFormattedActivities = (
  activities: Array<Activity>,
  filter: string,
) => {
  const newActivities: Array<Object> = activities
    .filter((a: Activity) => {
      if (!filter || filter === 'all') {
        return true;
      }
      const activityCompletionStatusId = a?.activityCompletionStatusId;
      if (!activityCompletionStatusId && filter === 'scheduled') {
        return true;
      }
      if (activityCompletionStatusId && filter === 'recorded') {
        return true;
      }
      return false;
    })
    .map((a: Activity) => {
      const activityTypeName = a.activityType ? a.activityType.name : '';
      const firstName = a?.prospect?.firstName;
      const lastName = a?.prospect?.lastName;
      const start = moment(a.startTime).format('hh:mm A');
      const end = moment(a.endTime).format('hh:mm A');
      const title = `${activityTypeName}: ${firstName} ${lastName}`;
      return {
        title,
        type: activityTypeName,
        id: a.id,
        timeRange: `${start} - ${end}`,
        toolTip: `${title} ${a.notes}`,
        start: new Date(a.startTime),
        end: new Date(a.endTime),
        allDay: a.allDay,
        desc: a.notes,
        activity: a,
      };
    });
  return newActivities;
};

export const formats: Object = {
  dayFormat: (date: string, culture: string, localizer: Function) =>
    localizer.format(date, 'MM/DD', culture),
};

export const displayEvent = (props: Object, e: Object, refreshFn: Function) => (
  <div
    style={{ height: '100%', width: '100%' }}
    onClick={() => handleClick(props, e, refreshFn)}
  >
    {e.title}
  </div>
);

export const getExpandedEventsForDate = (
  events: Array<Object>,
  selectedDay: Date,
) => {
  const selectedDayStart = moment(selectedDay).startOf('day');
  const selectedDayEnd = moment(selectedDay).endOf('day');

  return events.filter((event) => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);

    return (
      eventStart.isSameOrBefore(selectedDayEnd) &&
      eventEnd.isSameOrAfter(selectedDayStart)
    );
  });
};

const getCurrentViewportHeight = () =>
  typeof window !== 'undefined' ? window.innerHeight : 0;

export const getExpandedEventsPopupMaxHeight = (
  popupTop: number,
  viewportHeight: number,
) => {
  if (!viewportHeight) {
    return EXPANDED_EVENTS_POPUP_MAX_HEIGHT;
  }

  const availableViewportHeight =
    viewportHeight - popupTop - EXPANDED_EVENTS_POPUP_VIEWPORT_PADDING;

  return `${Math.max(
    EXPANDED_EVENTS_POPUP_MIN_HEIGHT,
    availableViewportHeight,
  )}px`;
};

export const createExpandedEventsPopup = ({
  calendarEvents,
  currentExpandedEventsPopup,
  selectedDay,
  targetRect,
  viewportHeight = getCurrentViewportHeight(),
}: Object) => {
  if (
    currentExpandedEventsPopup &&
    moment(currentExpandedEventsPopup.date).isSame(selectedDay, 'day')
  ) {
    return null;
  }

  const popupTop = targetRect.bottom + EXPANDED_EVENTS_POPUP_OFFSET.y;

  return {
    date: selectedDay,
    events: getExpandedEventsForDate(calendarEvents, selectedDay),
    maxHeight: getExpandedEventsPopupMaxHeight(popupTop, viewportHeight),
    position: {
      top: popupTop,
      left: targetRect.left + EXPANDED_EVENTS_POPUP_OFFSET.x,
    },
  };
};

export const getExpandedEventsPopupResetKey = ({
  currentFilter,
  selectedAssignee,
}: Object) => `${currentFilter || 'all'}:${selectedAssignee || 'all'}`;

export const getExpandedEventsDateFromTarget = (
  calendarDate: Date,
  showMoreTarget: HTMLElement,
) => {
  const monthRow = showMoreTarget.closest('.rbc-month-row');
  const monthView = showMoreTarget.closest('.rbc-month-view');

  if (!monthRow || !monthView) {
    return null;
  }

  const monthRows = Array.from(monthView.querySelectorAll('.rbc-month-row'));
  const rowIndex = monthRows.indexOf(monthRow);

  if (rowIndex < 0) {
    return null;
  }

  const targetRect = showMoreTarget.getBoundingClientRect();
  const monthViewRect = monthView.getBoundingClientRect();
  const dayWidth = monthViewRect.width / 7;
  const targetCenter = targetRect.left + targetRect.width / 2;
  const dayIndex = Math.max(
    0,
    Math.min(6, Math.floor((targetCenter - monthViewRect.left) / dayWidth)),
  );

  return moment(calendarDate)
    .startOf('month')
    .startOf('week')
    .add(rowIndex * 7 + dayIndex, 'days')
    .toDate();
};

export const renderExpandedEventsPopup = (
  props: Object,
  expandedEventsPopup: Object,
  refreshFn: Function,
) => {
  if (!expandedEventsPopup) {
    return null;
  }

  return (
    <div
      className="rbc-overlay assignee-calendar-expanded-events-popup"
      style={{
        position: 'fixed',
        top: expandedEventsPopup.position.top,
        left: expandedEventsPopup.position.left,
        maxHeight:
          expandedEventsPopup.maxHeight || EXPANDED_EVENTS_POPUP_MAX_HEIGHT,
      }}
    >
      <div className="rbc-overlay-header">
        {moment(expandedEventsPopup.date).format('dddd, MMMM D')}
      </div>
      {expandedEventsPopup.events.map((event) => {
        const eventStyles = eventStyleGetter(event);

        return (
          <button
            key={event.id}
            type="button"
            className="rbc-event assignee-calendar-expanded-events-popup__event"
            style={eventStyles.style}
            onClick={() => handleClick(props, { event }, refreshFn)}
          >
            {event.title}
          </button>
        );
      })}
    </div>
  );
};

export const handleClick = (props, { event: { activity } }, refreshFn: any) => {
  const customerArray = activity?.prospect?.customer ?? [];
  const customer = takeLast(1, customerArray)[0];
  let currentStage = customer?.status ?? 'prospect';
  let urlId = activity.prospectId;
  if (currentStage === 'Applicant') {
    currentStage = 'applicant';
    urlId = customer?.applicantCustomer?.applicant?.applicationId ?? urlId;
  } else if (currentStage === 'Current Resident') {
    currentStage = 'resident';
    urlId = customer?.resident?.residentId ?? urlId;
  } else if (currentStage === 'Prior Resident') {
    currentStage = 'prior-resident';
    urlId = customer?.resident?.residentId ?? urlId;
  }

  const stageInfo = {
    currentStage,
    urlId,
  };

  const activityTypesList = generateActivityTypesList(props);
  const users = generateUsers(props);
  const editPromise = activity.activityCompletionStatusId
    ? editCompletedActivity(props.store, props.intl, activity, stageInfo)
    : editPendingActivity(
        props.store,
        props.intl,
        activity,
        activityTypesList,
        users,
        activity.prospect,
        stageInfo,
      );

  const editFunction = () => onCreateActivityClick(props, activity.prospect);

  return editPromise
    .then((data: Activity) => {
      if (data.saveAndClose) {
        return props.actions.updateProspectActivity(data);
      } else if (data.saveAndAddNew) {
        props.actions.updateProspectActivity(data);
        return editFunction();
      } else {
        return props.actions.deleteProspectActivity(data);
      }
    })
    .then(() => {
      setTimeout(refreshFn, 1000);
    });
};

export const generateActivityTypesList = (props: Object): Array<any> => {
  const activityTypes = props.activityTypes.map((activityType) => ({
    value: activityType.id,
    text: activityType.translations[props.locale] || activityType.name,
    canBeOverlapped: activityType.canBeOverlapped,
    completionStatusOptions: activityType.completionStatusOptions,
    disabled: false,
  }));
  activityTypes.unshift({
    value: 'default',
    text: props.intl.formatMessage(messages.chooseOption),
    completionStatusOptions: [{ id: '' }],
    disabled: true,
    showForRecord: true,
  });
  return activityTypes;
};

export const onRecordActivityClick = (props: Object, prospect: Prospect) => {
  const activityTypesList = generateActivityTypesList(props);
  const users = generateUsers(props);
  recordActivity(
    props.store,
    props.intl,
    prospect,
    activityTypesList,
    users,
    'prospect',
    prospect.id,
  ).then((data: Activity) => {
    return props.actions.createProspectActivity(
      Object.assign(data, { customerStatus: 'Prospect' }),
    );
  });
};

export const onScheduleActivityClick = (props: Object, prospect: Prospect) => {
  const activityTypesList = generateActivityTypesList(props);
  const users = generateUsers(props);
  scheduleActivity(
    props.store,
    props.intl,
    prospect,
    activityTypesList,
    users,
    'prospect',
    prospect.id,
  ).then((data: Activity) => {
    return props.actions.createProspectActivity(
      Object.assign(data, { customerStatus: 'Prospect' }),
    );
  });
};

export const onCreateActivityClick = async (
  props: Object,
  prospect: Prospect,
) => {
  const activityTypesList = generateActivityTypesList(props);
  const users = generateUsers(props);
  const data: Activity = await createActivity({
    store: props.store,
    intl: props.intl,
    prospect: prospect,
    activityTypesList,
    assigneeList: users,
    stage: 'prospect',
    urlId: prospect.id,
    showViewProfileBtn: true,
  });
  props.actions.createProspectActivity({
    ...data,
    customerStatus: 'Prospect',
  });
};

export const generateUsers = (props: Object) => {
  return props.assignees
    ? props.assignees.map((user) => ({
        value: user.id,
        text: `${user.firstName} ${user.lastName}`,
        disabled: false,
      }))
    : [];
};

export const AssigneeCalendar = (props: Props) => {
  const [navigattedDate, setNavigatedDate] = useState(new Date(Date.now()));
  const [dates, setDates] = useState(null);
  const [currentView, setCurrentView] = useState('month');
  const [expandedEventsPopup, setExpandedEventsPopup] = useState(null);

  useEffect(() => {
    props.actions.getAllActivityTypes();
  }, []); // eslint-disable-line

  const { activities, activitiesLoading, refreshFn } = useAsyncAssigneeEvents(
    props.selectedProperty,
    props.selectedAssignee,
    dates,
  );
  const calendarEvents = generateFormattedActivities(
    activities,
    props.currentFilter,
  );

  const expandedEventsPopupResetKey = getExpandedEventsPopupResetKey(props);
  const clearExpandedEventsPopup = () => setExpandedEventsPopup(null);

  useEffect(() => {
    setExpandedEventsPopup(null);
  }, [expandedEventsPopupResetKey]);

  const handleExpandedEventsClick = (event) => {
    const showMoreTarget = event.target.closest('.rbc-show-more');

    if (!showMoreTarget) {
      return;
    }

    const selectedDay = getExpandedEventsDateFromTarget(
      navigattedDate,
      showMoreTarget,
    );

    if (!selectedDay) {
      return;
    }

    const targetRect = showMoreTarget.getBoundingClientRect();

    event.preventDefault();
    event.stopPropagation();

    setExpandedEventsPopup((currentExpandedEventsPopup) =>
      createExpandedEventsPopup({
        calendarEvents,
        currentExpandedEventsPopup,
        selectedDay,
        targetRect,
      }),
    );
  };

  return !activitiesLoading ? (
    <div
      className="assignee-calendar"
      style={ASSIGNEE_CALENDAR_CONTAINER_STYLE}
      onClickCapture={handleExpandedEventsClick}
    >
      <Calendar
        popup={false}
        formats={formats}
        view={currentView}
        date={navigattedDate}
        culture={props.locale}
        events={calendarEvents}
        messages={generateMessages(props.intl)}
        onView={(view) => {
          clearExpandedEventsPopup();
          setCurrentView(view);
        }}
        onRangeChange={(dates) => setDates(dates)}
        onNavigate={(date) => {
          clearExpandedEventsPopup();
          setNavigatedDate(date);
        }}
        components={{ event: (e) => displayEvent(props, e, refreshFn) }}
        tooltipAccessor={(e) => updateTooltip(e, props, currentView)}
        eventPropGetter={eventStyleGetter}
        defaultDate={new Date(Date.now())}
        scrollToTime={new Date(Date.now())}
      />
      {renderExpandedEventsPopup(props, expandedEventsPopup, refreshFn)}
    </div>
  ) : (
    <Spinner />
  );
};

export const mapStateToProps = ({ app, home }: any) => {
  return {
    activities: home.activities,
    activityTypes: app.activityTypes,
    assignees: app.allAssignees,
    selectedProperty: app.selectedProperty,
  };
};

export const mapDispatchToProps = (dispatch: any): Object => {
  const actions = bindActionCreators(
    {
      ...prospectActions,
      getAllActivityTypes,
      getAllActivitiesByAssignee,
      clearActivitiesByAssignee,
    },
    dispatch,
  );
  return { actions };
};

const connected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AssigneeCalendar);

export default withLDConsumer()(connected);
